import { Routes } from '@angular/router';
import { VersionGuard } from './shared/guards/version.guard';
import { AuthGuard } from './shared/guards/auth.guard';
import { PublicGuard } from './shared/guards/public.guard';
import { PrivacypolicyGuard } from './shared/guards/privacypolicy.guard';
import { AlreadyDownloadedGuard } from './shared/guards/already-downloaded.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full'
  },
  {
    path: 'download',
    loadComponent: () => import('./shared/components/download/download.component').then(m => m.DownloadComponent),
    canActivate: [AlreadyDownloadedGuard]
  },
  {
    path: 'update-needed',
    loadComponent: () => import('./pages/update-needed/update-needed.page').then(m => m.UpdateNeededPage)
  },
  {
    path: 'password-reset/:id',
    loadComponent: () => import('./pages/login/password-reset/password-reset.page').then(m => m.PasswordResetPage)
  },
  {
    path: 'forgot-pass',
    loadComponent: () => import('./pages/login/forgot-pass/forgot-pass.page').then(m => m.ForgotPassPage)
  },
  {
    path: 'native-neptun-login',
    loadComponent: () => import('./pages/login/native-neptun-login/native-neptun-login.page').then(m => m.NativeNeptunLoginPage)
  },
  {
    path: 'native-neptun-login-redirect',
    loadComponent: () => import('./pages/login/native-neptun-login-redirect/native-neptun-login-redirect.page').then(m => m.NativeNeptunLoginRedirectPage)
  },
  {
    path: 'native-neptun-login-post',
    loadComponent: () => import('./pages/login/native-neptun-login-post/native-neptun-login-post.page').then(m => m.NativeNeptunLoginPostPage)
  },
  {
    path: 'email-confirmation/:token',
    loadComponent: () => import('./pages/login/email-confirmation/email-confirmation.page').then(m => m.EmailConfirmationPage),
  },
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home.page').then(m => m.HomePage),
    canActivate: [AuthGuard, VersionGuard]
  },
  {
    path: 'select-subject',
    loadComponent: () => import('./pages/subjects/subjects.page').then(m => m.SubjectsPage),
    canActivate: [AuthGuard, VersionGuard]
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.page').then(m => m.LoginPage),
    canActivate: [PublicGuard]
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./pages/registration/privacy-policy/privacy-policy.page').then(m => m.PrivacyPolicyPage),
    canActivate: [PrivacypolicyGuard, VersionGuard]
  },
  {
    path: 'registration',
    loadComponent: () => import('./pages/registration/registration.page').then(m => m.RegistrationPage),
    canActivate: [PublicGuard, VersionGuard],
  },
  {
    path: 'profile',
    loadComponent: () => import('./pages/profile/profile.page').then(m => m.ProfilePage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'profile-data',
    loadComponent: () => import('./pages/profile/profile-data/profile-data.page').then(m => m.ProfileDataPage),
    canActivate: [AuthGuard, VersionGuard]
  },
  {
    path: 'profile-agreement',
    loadComponent: () => import('./pages/profile/profile-agreement/profile-agreement.page').then(m => m.ProfileAgreementPage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'profile-eula',
    loadComponent: () => import('./pages/profile/profile-eula/profile-eula.page').then(m => m.ProfileEulaPage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'profile-terms-and-conditions',
    loadComponent: () => import('./pages/profile/profile-terms-and-conditions/profile-terms-and-conditions.page').then(m => m.ProfileTermsAndConditionsPage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'groups',
    loadComponent: () => import('./pages/profile/groups/groups.page').then(m => m.GroupsPage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'join-group',
    loadComponent: () => import('./pages/profile/groups/join-group/join-group.page').then(m => m.JoinGroupPage),
    canActivate: [AuthGuard, VersionGuard]
  },
  {
    path: 'feedback',
    loadComponent: () => import('./pages/profile/feedback/feedback.page').then(m => m.FeedbackPage),
    canActivate: [AuthGuard, VersionGuard]
  },
  {
    path: 'quiz-feedback',
    loadComponent: () => import('./pages/quiz-feedback/quiz-feedback.page').then(m => m.QuizFeedbackPage),
    canActivate: [AuthGuard, VersionGuard]
  },
  {
    path: 'homelearning',
    loadComponent: () => import('./pages/home-learning/home-learning.page').then(m => m.HomeLearningPage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'homework/:learningTaskId',
    loadComponent: () => import('./pages/homework/homework.page').then(m => m.HomeworkPage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'exam/:learningTaskId',
    loadComponent: () => import('./pages/exam/exam.page').then(m => m.ExamPage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'classwork/:learningTaskId',
    loadComponent: () => import('./pages/classwork/classwork.page').then(m => m.ClassworkPage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'select-fields/:id',
    loadComponent: () => import('./pages/subject/subject.page').then(m => m.SubjectPage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'select-knowledge-elements/:fieldId',
    loadComponent: () => import('./pages/knowledge-elements/knowledge-elements.page').then(m => m.KnowledgeElementsPage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'marketplace',
    loadComponent: () => import('./pages/marketplace/marketplace.page').then(m => m.MarketplacePage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: 'payment-result',
    loadComponent: () => import('./pages/payment-result/payment-result.page').then(m => m.PaymentResultPage)
  },
  {
    path: 'change-password',
    loadComponent: () => import('./pages/profile/change-password/change-password.page').then(m => m.ChangePasswordPage),
    canActivate: [AuthGuard, VersionGuard],
  },
  {
    path: '**',
    redirectTo: 'home',
    pathMatch: 'prefix',
  },
];
